/**
 * qrcode.ts — dependency-free QR Code (Model 2) encoder.
 *
 * Why in-tree: the Live tab (and, later, event/landing/hub share surfaces that
 * currently say "QR coming soon") needs a scannable code for a link the page
 * ALREADY holds. A QR symbol is a deterministic encoding of that string — no
 * server data, no fixture, nothing fabricated — so generating it client-side is
 * real behaviour, not a stand-in. No npm dependency is available to this app,
 * hence this compact implementation.
 *
 * Scope: byte mode (UTF-8), error-correction level M, versions 1–10 (up to 213
 * UTF-8 bytes — journey/share links and check-in codes are far shorter). `encodeQr` throws for
 * longer input so callers surface an honest message instead of a broken symbol.
 * Mask is chosen by the standard penalty evaluation over all eight patterns.
 */

/* ── GF(256) arithmetic (primitive polynomial 0x11D) ── */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255];
})();
const gfMul = (a: number, b: number): number => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

/** Reed–Solomon generator polynomial of the given degree. */
function rsGenerator(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i += 1) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] ^= poly[j];
      next[j + 1] ^= gfMul(poly[j], EXP[i]);
    }
    poly = next;
  }
  return poly;
}

function rsRemainder(data: number[], ecLen: number): number[] {
  const gen = rsGenerator(ecLen);
  const res = new Array<number>(ecLen).fill(0);
  for (const byte of data) {
    const factor = byte ^ res[0];
    res.shift();
    res.push(0);
    for (let i = 0; i < ecLen; i += 1) res[i] ^= gfMul(gen[i + 1], factor);
  }
  return res;
}

/* ── Per-version tables, error-correction level M ──
 * [totalCodewords, ecPerBlock, group1Blocks, group1DataCodewords, group2Blocks, group2DataCodewords] */
const VERSIONS: [number, number, number, number, number, number][] = [
  [26, 10, 1, 16, 0, 0],    // 1
  [44, 16, 1, 28, 0, 0],    // 2
  [70, 26, 1, 44, 0, 0],    // 3
  [100, 18, 2, 32, 0, 0],   // 4
  [134, 24, 2, 43, 0, 0],   // 5
  [172, 16, 4, 27, 0, 0],   // 6
  [196, 18, 4, 31, 0, 0],   // 7
  [242, 22, 2, 38, 2, 39],  // 8
  [292, 22, 3, 36, 2, 37],  // 9
  [346, 26, 4, 43, 1, 44],  // 10
];
const ALIGN_CENTERS: number[][] = [
  [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
];
const REMAINDER_BITS = [0, 7, 7, 7, 7, 7, 0, 0, 0, 0];
const EC_LEVEL_BITS = 0b00; // level M

export interface QrSymbol {
  /** Modules per side, including the quiet-zone-free symbol itself. */
  size: number;
  /** `modules[row][col]` — true = dark. */
  modules: boolean[][];
  version: number;
}

/** Encode `text` as a QR symbol. Throws when the text exceeds version 10 at level M. */
export function encodeQr(text: string): QrSymbol {
  const bytes = Array.from(new TextEncoder().encode(text));
  const version = pickVersion(bytes.length);
  const [total, ecPerBlock, g1, g1Data, g2, g2Data] = VERSIONS[version - 1];
  const dataCodewords = g1 * g1Data + g2 * g2Data;

  /* Bit stream: mode indicator (byte = 0100), character count, payload. */
  const bits: number[] = [];
  const push = (value: number, len: number) => {
    for (let i = len - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
  };
  push(0b0100, 4);
  push(bytes.length, version < 10 ? 8 : 16);
  bytes.forEach((b) => push(b, 8));

  const capacityBits = dataCodewords * 8;
  for (let i = 0; i < 4 && bits.length < capacityBits; i += 1) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);
  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    codewords.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  }
  const PAD = [0xec, 0x11];
  for (let p = 0; codewords.length < dataCodewords; p += 1) codewords.push(PAD[p % 2]);

  /* Split into blocks, compute EC, interleave. */
  const blocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;
  for (let b = 0; b < g1 + g2; b += 1) {
    const len = b < g1 ? g1Data : g2Data;
    const block = codewords.slice(offset, offset + len);
    offset += len;
    blocks.push(block);
    ecBlocks.push(rsRemainder(block, ecPerBlock));
  }
  const interleaved: number[] = [];
  const maxData = Math.max(g1Data, g2Data);
  for (let i = 0; i < maxData; i += 1) {
    blocks.forEach((block) => { if (i < block.length) interleaved.push(block[i]); });
  }
  for (let i = 0; i < ecPerBlock; i += 1) {
    ecBlocks.forEach((block) => interleaved.push(block[i]));
  }
  if (interleaved.length !== total) throw new Error(`QR: codeword count ${interleaved.length} != ${total}`);

  /* Matrix: function patterns first, then data, then the best mask. */
  const size = version * 4 + 17;
  const modules: (boolean | null)[][] = Array.from({ length: size }, () => new Array<boolean | null>(size).fill(null));
  const reserved: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const set = (r: number, c: number, dark: boolean) => { modules[r][c] = dark; reserved[r][c] = true; };

  const finder = (row: number, col: number) => {
    for (let r = -1; r <= 7; r += 1) {
      for (let c = -1; c <= 7; c += 1) {
        const rr = row + r;
        const cc = col + c;
        if (rr < 0 || cc < 0 || rr >= size || cc >= size) continue;
        const ring = Math.max(Math.abs(r - 3), Math.abs(c - 3));
        set(rr, cc, ring !== 2 && ring <= 3);
      }
    }
  };
  finder(0, 0); finder(0, size - 7); finder(size - 7, 0);

  for (let i = 8; i < size - 8; i += 1) {
    const dark = i % 2 === 0;
    set(6, i, dark);
    set(i, 6, dark);
  }
  // Alignment patterns sit at every centre pair EXCEPT the three that would
  // overlap a finder. They may legitimately overlap the timing patterns
  // (versions 7+), where the module values agree anyway.
  const inFinder = (r: number, c: number) =>
    (r <= 8 && c <= 8) || (r <= 8 && c >= size - 9) || (r >= size - 9 && c <= 8);
  ALIGN_CENTERS[version - 1].forEach((cy) => {
    ALIGN_CENTERS[version - 1].forEach((cx) => {
      if (inFinder(cy, cx)) return;
      for (let r = -2; r <= 2; r += 1) {
        for (let c = -2; c <= 2; c += 1) {
          set(cy + r, cx + c, Math.max(Math.abs(r), Math.abs(c)) !== 1);
        }
      }
    });
  });
  set(size - 8, 8, true); // dark module

  /* Reserve the format areas (values written after masking). */
  for (let i = 0; i <= 8; i += 1) {
    if (!reserved[8][i]) { modules[8][i] = false; reserved[8][i] = true; }
    if (!reserved[i][8]) { modules[i][8] = false; reserved[i][8] = true; }
  }
  for (let i = 0; i < 8; i += 1) {
    if (!reserved[8][size - 1 - i]) { modules[8][size - 1 - i] = false; reserved[8][size - 1 - i] = true; }
    if (!reserved[size - 1 - i][8]) { modules[size - 1 - i][8] = false; reserved[size - 1 - i][8] = true; }
  }
  if (version >= 7) {
    const vBits = versionBits(version);
    for (let i = 0; i < 18; i += 1) {
      const bit = ((vBits >>> i) & 1) === 1;
      const a = Math.floor(i / 3);
      const b = i % 3;
      set(size - 11 + b, a, bit);
      set(a, size - 11 + b, bit);
    }
  }

  /* Data placement: two-module columns, right to left, alternating direction. */
  const stream: number[] = [];
  interleaved.forEach((cw) => { for (let i = 7; i >= 0; i -= 1) stream.push((cw >>> i) & 1); });
  for (let i = 0; i < REMAINDER_BITS[version - 1]; i += 1) stream.push(0);
  let idx = 0;
  let upward = true;
  let col = size - 1;
  while (col > 0) {
    if (col === 6) col -= 1; // the vertical timing column is never a data column
    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step;
      for (const c of [col, col - 1]) {
        if (c < 0 || reserved[row][c]) continue;
        modules[row][c] = idx < stream.length ? stream[idx] === 1 : false;
        idx += 1;
      }
    }
    upward = !upward;
    col -= 2;
  }

  const base = modules.map((row) => row.map((m) => m === true));
  let best: boolean[][] | null = null;
  let bestPenalty = Infinity;
  for (let mask = 0; mask < 8; mask += 1) {
    const candidate = base.map((row) => [...row]);
    for (let r = 0; r < size; r += 1) {
      for (let c = 0; c < size; c += 1) {
        if (!reserved[r][c] && maskBit(mask, r, c)) candidate[r][c] = !candidate[r][c];
      }
    }
    writeFormat(candidate, size, mask);
    const p = penalty(candidate, size);
    if (p < bestPenalty) { bestPenalty = p; best = candidate; }
  }
  return { size, modules: best as boolean[][], version };
}

function pickVersion(byteLength: number): number {
  for (let v = 1; v <= VERSIONS.length; v += 1) {
    const [, , g1, g1Data, g2, g2Data] = VERSIONS[v - 1];
    const dataBits = (g1 * g1Data + g2 * g2Data) * 8;
    const headerBits = 4 + (v < 10 ? 8 : 16);
    if (headerBits + byteLength * 8 <= dataBits) return v;
  }
  throw new Error("QR: text too long for version 10 at error-correction level M");
}

function maskBit(mask: number, r: number, c: number): boolean {
  switch (mask) {
    case 0: return (r + c) % 2 === 0;
    case 1: return r % 2 === 0;
    case 2: return c % 3 === 0;
    case 3: return (r + c) % 3 === 0;
    case 4: return (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0;
    case 5: return ((r * c) % 2) + ((r * c) % 3) === 0;
    case 6: return ((((r * c) % 2) + ((r * c) % 3)) % 2) === 0;
    default: return ((((r + c) % 2) + ((r * c) % 3)) % 2) === 0;
  }
}

/** BCH(15,5) format information, XOR-masked per the spec. */
function formatBits(mask: number): number {
  const data = (EC_LEVEL_BITS << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

/** BCH(18,6) version information. */
function versionBits(version: number): number {
  let rem = version;
  for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  return (version << 12) | rem;
}

/**
 * The 15 format bits, twice, at the spec's positions. `m[row][col]`.
 *
 * Copy 1 wraps the top-left finder: bits 0–5 down COLUMN 8 (rows 0–5), bit 6 at
 * (7,8), bit 7 at (8,8), bit 8 at (8,7), bits 9–14 along ROW 8 (cols 5→0).
 * Copy 2: bits 0–7 along ROW 8 from the right edge, bits 8–14 down COLUMN 8
 * below the dark module.
 *
 * This used to write both copies TRANSPOSED (row 8 for column 8 and vice
 * versa). The bits themselves were right, so every symbol looked plausible, but
 * a reader takes the format word from the wrong cells, gets the wrong level or
 * mask, and cannot decode the symbol at all.
 */
function writeFormat(m: boolean[][], size: number, mask: number): void {
  const bits = formatBits(mask);
  const bit = (i: number) => ((bits >>> i) & 1) === 1;
  for (let i = 0; i <= 5; i += 1) m[i][8] = bit(i);
  m[7][8] = bit(6);
  m[8][8] = bit(7);
  m[8][7] = bit(8);
  for (let i = 9; i <= 14; i += 1) m[8][14 - i] = bit(i);
  for (let i = 0; i <= 7; i += 1) m[8][size - 1 - i] = bit(i);
  for (let i = 8; i <= 14; i += 1) m[size - 15 + i][8] = bit(i);
  m[size - 8][8] = true; // dark module
}

/** The four standard penalty rules — used only to choose between masks. */
function penalty(m: boolean[][], size: number): number {
  let score = 0;
  const runScore = (run: number) => (run >= 5 ? run - 2 : 0);
  for (let r = 0; r < size; r += 1) {
    let run = 1;
    for (let c = 1; c < size; c += 1) {
      if (m[r][c] === m[r][c - 1]) run += 1; else { score += runScore(run); run = 1; }
    }
    score += runScore(run);
  }
  for (let c = 0; c < size; c += 1) {
    let run = 1;
    for (let r = 1; r < size; r += 1) {
      if (m[r][c] === m[r - 1][c]) run += 1; else { score += runScore(run); run = 1; }
    }
    score += runScore(run);
  }
  for (let r = 0; r < size - 1; r += 1) {
    for (let c = 0; c < size - 1; c += 1) {
      const v = m[r][c];
      if (v === m[r][c + 1] && v === m[r + 1][c] && v === m[r + 1][c + 1]) score += 3;
    }
  }
  const pattern = [true, false, true, true, true, false, true];
  const hasPattern = (get: (i: number) => boolean, start: number) =>
    pattern.every((p, i) => get(start + i) === p);
  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c + 7 <= size; c += 1) {
      if (hasPattern((i) => m[r][i], c)) score += 40;
    }
  }
  for (let c = 0; c < size; c += 1) {
    for (let r = 0; r + 7 <= size; r += 1) {
      if (hasPattern((i) => m[i][c], r)) score += 40;
    }
  }
  let dark = 0;
  m.forEach((row) => row.forEach((v) => { if (v) dark += 1; }));
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;
  return score;
}
